import { Controller } from "@hotwired/stimulus"

// Sticky mini audio player. Mirrors the currently active <audio> inside an
// inline player block (typically the `.audio-versions-card`) and surfaces
// itself when the inline player scrolls out of view, so the user can keep
// listening while reading the rest of the page.
export default class extends Controller {
  static targets = [
    "playBtn",
    "playIcon",
    "pauseIcon",
    "currentTime",
    "duration",
    "seekRange",
    "eyebrow"
  ]

  static values = {
    sourceSelector: { type: String, default: ".audio-versions-card" }
  }

  connect() {
    this.sourceEl = document.querySelector(this.sourceSelectorValue)
    if (!this.sourceEl) {
      this.element.hidden = true
      return
    }

    this.boundAudios = new Set()
    this._activeAudio = null
    this._everPlayed = false
    this._sourceVisible = true

    this._scanAudios()
    this._refreshActiveAudio()
    this._setupIntersectionObserver()
    this._setupMutationObserver()
    this._refreshUI()
  }

  disconnect() {
    if (this.intersectionObserver) this.intersectionObserver.disconnect()
    if (this.mutationObserver) this.mutationObserver.disconnect()
    this.boundAudios.forEach((audio) => this._unbindAudio(audio))
    this.boundAudios.clear()
  }

  // -- Public actions ------------------------------------------------------
  togglePlay(event) {
    event.preventDefault()
    const audio = this._activeAudio
    if (!audio) return

    if (audio.paused) {
      const playPromise = audio.play()
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {})
      }
    } else {
      audio.pause()
    }
  }

  seek(event) {
    const audio = this._activeAudio
    if (!audio) return
    const duration = Number.isFinite(audio.duration) ? audio.duration : 0
    if (duration <= 0) return
    const percent = Math.max(0, Math.min(100, Number(event.target.value)))
    audio.currentTime = (percent / 100) * duration
  }

  expand(event) {
    event.preventDefault()
    if (this.sourceEl) {
      this.sourceEl.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  // -- Internals -----------------------------------------------------------
  _scanAudios() {
    const audios = Array.from(this.sourceEl.querySelectorAll("audio"))
    audios.forEach((audio) => this._bindAudio(audio))
  }

  _bindAudio(audio) {
    if (this.boundAudios.has(audio)) return

    const handlers = {
      play: () => this._handlePlay(audio),
      pause: () => this._handleStateChange(audio),
      ended: () => this._handleStateChange(audio),
      timeupdate: () => this._handleTimeUpdate(audio),
      loadedmetadata: () => this._handleTimeUpdate(audio),
      durationchange: () => this._handleTimeUpdate(audio)
    }
    audio.__smpHandlers = handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      audio.addEventListener(event, handler)
    })
    this.boundAudios.add(audio)
  }

  _unbindAudio(audio) {
    const handlers = audio.__smpHandlers
    if (!handlers) return
    Object.entries(handlers).forEach(([event, handler]) => {
      audio.removeEventListener(event, handler)
    })
    delete audio.__smpHandlers
  }

  _refreshActiveAudio() {
    const activePane =
      this.sourceEl.querySelector(".audio-versions-pane.is-active") ||
      this.sourceEl
    const audio = activePane.querySelector("audio")

    if (audio && !this.boundAudios.has(audio)) this._bindAudio(audio)

    this._activeAudio = audio || null

    if (this.hasEyebrowTarget) {
      const labelEl = activePane.querySelector(".audio-versions-tab.is-active span") ||
        activePane.querySelector(".audio-versions-pane-title")
      if (labelEl) {
        this.eyebrowTarget.textContent = labelEl.textContent.trim()
      }
    }

    this._refreshUI()
  }

  _setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this._sourceVisible = entry.isIntersecting
          this._updateVisibility()
        })
      },
      { rootMargin: "0px 0px -40% 0px", threshold: 0 }
    )
    this.intersectionObserver.observe(this.sourceEl)
  }

  _setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      let needsRefresh = false
      let scanForNewAudios = false

      mutations.forEach((m) => {
        if (m.type === "attributes" && (m.attributeName === "hidden" || m.attributeName === "class")) {
          needsRefresh = true
        }
        if (m.type === "childList" && m.addedNodes.length > 0) {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE && (node.tagName === "AUDIO" || node.querySelector?.("audio"))) {
              scanForNewAudios = true
            }
          })
        }
      })

      if (scanForNewAudios) this._scanAudios()
      if (needsRefresh) this._refreshActiveAudio()
    })
    this.mutationObserver.observe(this.sourceEl, {
      attributes: true,
      subtree: true,
      childList: true,
      attributeFilter: ["hidden", "class"]
    })
  }

  _handlePlay(audio) {
    if (audio !== this._activeAudio) {
      this._activeAudio = audio
    }
    this._everPlayed = true
    this._refreshUI()
    this._updateVisibility()
  }

  _handleStateChange(audio) {
    if (audio === this._activeAudio) this._refreshPlayState()
  }

  _handleTimeUpdate(audio) {
    if (audio === this._activeAudio) this._refreshTime()
  }

  _refreshUI() {
    this._refreshPlayState()
    this._refreshTime()
  }

  _refreshPlayState() {
    const audio = this._activeAudio
    const isPlaying = !!audio && !audio.paused && !audio.ended
    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("is-hidden", isPlaying)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("is-hidden", !isPlaying)
    if (this.hasPlayBtnTarget) {
      this.playBtnTarget.setAttribute("aria-pressed", isPlaying ? "true" : "false")
    }
  }

  _refreshTime() {
    const audio = this._activeAudio
    const duration = audio && Number.isFinite(audio.duration) ? audio.duration : 0
    const current = audio && Number.isFinite(audio.currentTime) ? audio.currentTime : 0

    if (this.hasCurrentTimeTarget) this.currentTimeTarget.textContent = this._formatTime(current)
    if (this.hasDurationTarget) this.durationTarget.textContent = this._formatTime(duration)

    if (this.hasSeekRangeTarget) {
      const percent = duration > 0 ? (current / duration) * 100 : 0
      this.seekRangeTarget.value = String(percent)
    }
  }

  _updateVisibility() {
    const shouldShow = this._everPlayed && !this._sourceVisible
    this.element.classList.toggle("is-visible", shouldShow)

    if (shouldShow) {
      this.element.hidden = false
    } else if (!this._everPlayed) {
      this.element.hidden = true
    }
  }

  _formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return "0:00"
    const total = Math.floor(seconds)
    const minutes = Math.floor(total / 60)
    const secs = total % 60
    return `${minutes}:${String(secs).padStart(2, "0")}`
  }
}
